/** Feed de aprovação da cliente. Território creme. Onda 15. */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { PostDTO } from "@unik/shared";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { BrandMark } from "../../ui/BrandMark";
import { Button } from "../../ui/Button";
import { Card, EmptyState } from "../../ui/Card";
import { InstallPrompt } from "../../ui/InstallPrompt";
import { FullScreenLoader } from "../../ui/ProtectedRoute";

interface Feed {
  posts: PostDTO[];
  summary: { pending: number; approved: number; total: number };
}

export function Aprovacoes() {
  const navigate = useNavigate();
  const { session, setSession } = useSession();
  const [feed, setFeed] = useState<Feed | null>(null);
  const [revising, setRevising] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.get<Feed>("/approval/feed").then(setFeed).catch(() => setFeed({ posts: [], summary: { pending: 0, approved: 0, total: 0 } }));
  }, []);

  function removeFromFeed(id: string, approved: boolean) {
    setFeed((f) =>
      f === null ? f : {
        posts: f.posts.filter((p) => p.id !== id),
        summary: {
          pending: f.summary.pending - 1,
          approved: f.summary.approved + (approved ? 1 : 0),
          total: f.summary.total,
        },
      },
    );
  }

  async function aprovar(id: string) {
    setBusy(id);
    setErro(null);
    try {
      await api.post(`/approval/posts/${id}/approve`);
      removeFromFeed(id, true);
    } catch {
      setErro("Não foi possível registrar. Verifique sua conexão e tente de novo.");
    } finally {
      setBusy(null);
    }
  }

  async function enviarRevisao(id: string) {
    setBusy(id);
    setErro(null);
    try {
      await api.post(`/approval/posts/${id}/revise`, { comment });
      setRevising(null);
      setComment("");
      removeFromFeed(id, false);
    } catch {
      setErro("Não foi possível enviar seu pedido. Tente de novo.");
    } finally {
      setBusy(null);
    }
  }

  async function sair() {
    await api.post("/auth/logout").catch(() => undefined);
    setSession(null);
    navigate("/entrar", { replace: true });
  }

  if (feed === null) return <FullScreenLoader />;

  return (
    <div className="app-shell min-h-dvh">
      <header className="sticky top-0 z-[var(--z-sticky)] flex items-center justify-between border-b border-border-subtle bg-surface-base/80 px-5 py-4 backdrop-blur">
        <BrandMark variant="vinho" className="h-5" />
        <Button variant="ghost" size="sm" onClick={sair}>Sair</Button>
      </header>

      <main className="mx-auto w-full max-w-md px-5 py-6">
        <h1 className="type-h2 text-text-cream">Olá, {session?.name ?? "bem-vinda"}.</h1>
        <p className="type-body-sm mt-1 text-text-secondary">
          {feed.summary.total > 0
            ? `${feed.summary.approved} de ${feed.summary.total} aprovados neste mês.`
            : "Assim que houver conteúdo, ele aparece aqui."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Link to="/desempenho">
            <Button variant="secondary" size="sm">Ver meu desempenho</Button>
          </Link>
          <Link to="/briefing">
            <Button variant="ghost" size="sm">Meu diagnóstico</Button>
          </Link>
          <Link to="/meu-mapa">
            <Button variant="ghost" size="sm">🗺️ Meu mapa</Button>
          </Link>
          <InstallPrompt />
        </div>

        {erro !== null && (
          <p role="alert" className="type-caption mt-3 rounded-[var(--radius-control-sm)] bg-[var(--danger-soft)] px-3 py-2 text-danger">
            {erro}
          </p>
        )}

        {feed.posts.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              title="Tudo aprovado! 💛"
              description="Você está em dia. Quando houver novidade, a gente te avisa."
            />
          </div>
        ) : (
          <div className="mt-6">
            {feed.posts.map((p) => (
              <Card key={p.id} glass className="mb-5 overflow-hidden !p-0">
                {p.media[0] !== undefined && (
                  <div className="relative">
                    <img src={p.media[0].publicUrl} alt="" className="w-full" />
                    {p.media.length > 1 && (
                      <span className="type-caption absolute right-3 top-3 rounded-full bg-black/50 px-2 py-0.5 text-white">
                        1/{p.media.length}
                      </span>
                    )}
                  </div>
                )}
                <div className="p-4">
                  {p.caption !== "" && (
                    <p className="type-body whitespace-pre-wrap text-text-primary">{p.caption}</p>
                  )}

                  {revising === p.id ? (
                    <div className="mt-4">
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        placeholder="O que você gostaria de ajustar?"
                        className="w-full rounded-[var(--radius-control-sm)] border border-border-default bg-surface-sunken px-3 py-2 text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
                      />
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" onClick={() => enviarRevisao(p.id)} loading={busy === p.id}>
                          Enviar pedido
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setRevising(null);
                            setComment("");
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5 flex items-center gap-3">
                      <Button
                        size="lg"
                        shape="invite"
                        fullWidth
                        loading={busy === p.id}
                        onClick={() => aprovar(p.id)}
                      >
                        Aprovar
                      </Button>
                      <Button size="lg" variant="secondary" onClick={() => setRevising(p.id)}>
                        Revisar
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
